/**
 * Tags — one vocabulary, attached to things of different kinds.
 *
 * The question that produced this was narrow: a PM agent should not carry
 * technical memories in its system prompt, and a technical agent should not
 * carry the PM's. The narrow answer would have been an `audience` column on
 * `agent_memories`. It was rejected because the same question keeps arriving in
 * different clothes — which memories does this agent load, which local commands
 * does this project offer, which command templates matter here — and three
 * bespoke columns would be three vocabularies to keep aligned by hand.
 *
 * ── Why the vocabulary is rows and not a union of literals ────────────────
 *
 * The rule this codebase already follows: what the *code* branches on lives in
 * a literal union (`AgentMemoryScope`, `ManagedAgentMode`, `AgentMemoryStatus`);
 * what an *admin* configures lives in a table with a screen (`agents`, `teams`,
 * `agent_memories`). No line of server code will ever ask whether a tag is
 * `tecnico` — tags are opaque to it, used only for set intersection — so they
 * are the second kind. If a branch on a specific tag ever becomes necessary,
 * that is the signal the concept was really an enum and this was the wrong
 * home for it.
 *
 * `TaggableType` is the exception that proves it, and is a literal union: the
 * code does branch on it, because deleting an agent and deleting a memory clean
 * up different tables.
 *
 * ── Namespaces are not decoration ─────────────────────────────────────────
 *
 * Without them the matching rule collapses into a plain intersection, and a
 * plain intersection cannot express the case that started all of this. Consider
 * a memory tagged `audiencia:tecnico`, read by a PM agent working in a project
 * tagged `dominio:android`. A flat union of the context's tags would ask "do
 * they share anything?", the project's `dominio:android` would have nothing to
 * do with it, and the memory would be excluded — correct here, but only by
 * accident. Turn it around: a memory tagged `dominio:android` should reach that
 * PM agent, because it never claimed to be about audience at all.
 *
 * So the rule is per namespace — intersection *within* a namespace, conjunction
 * *across* namespaces — and nobody has to declare which holder owns which
 * namespace. See `@taskin-code/shared/tagMatch`, which is where it is written
 * once for every consumer.
 *
 * The empty namespace is a legal value rather than a null, and is exactly what
 * it looks like: an unqualified tag, matched against the context's other
 * unqualified tags. It is `''` and not `NULL` because the uniqueness index
 * covers `(namespace, slug)` and Postgres treats NULLs as distinct — the same
 * trap that cost migration 043 three separate partial indexes.
 *
 * Schema-only, like the rest of this package.
 */
import { Schema } from "effect";

import { IsoDateTime, ProjectId, TagId, TrimmedNonEmptyString } from "./baseSchemas";

/**
 * Bounds. A tag is read in a picker and rendered as a chip, so `label` is
 * short by intent rather than by defence, and `description` exists for the
 * admin screen that has to explain to a colleague what `dominio:android`
 * covers.
 */
export const TAG_NAMESPACE_MAX_LENGTH = 32;
export const TAG_SLUG_MAX_LENGTH = 48;
export const TAG_LABEL_MAX_LENGTH = 60;
export const TAG_DESCRIPTION_MAX_LENGTH = 280;

/**
 * How many tags one thing may carry.
 *
 * Not a storage concern — it is the matching rule. Every namespace a thing
 * declares is one more condition it has to satisfy, so a heavily tagged memory
 * is a memory that reaches almost nobody. A cap keeps that failure loud (the
 * editor refuses) instead of silent (the memory quietly stops appearing).
 */
export const TAG_MAX_PER_TARGET = 16;

/**
 * The shape shared by namespace and slug: lowercase, digits, single hyphens.
 *
 * Same reasoning as `AgentMemorySlug` — these are read aloud in admin screens
 * and typed into a search box, so nothing that needs quoting or normalising.
 */
const TAG_TOKEN_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/** A namespace, or `''` for an unqualified tag. Never null — see the header. */
export const TagNamespace = Schema.String.check(
  Schema.isMaxLength(TAG_NAMESPACE_MAX_LENGTH),
  Schema.isPattern(new RegExp(`^$|${TAG_TOKEN_PATTERN.source}`)),
);
export type TagNamespace = typeof TagNamespace.Type;

export const TagSlug = TrimmedNonEmptyString.check(
  Schema.isMaxLength(TAG_SLUG_MAX_LENGTH),
  Schema.isPattern(TAG_TOKEN_PATTERN),
);
export type TagSlug = typeof TagSlug.Type;

/**
 * Chip colour, as a six-digit hex.
 *
 * Free-form rather than a fixed palette: the point of a colour here is that a
 * person recognises *their* grouping at a glance, and a palette chosen in this
 * file cannot know what those groupings are. Nullable because most tags will
 * never set one, and a default assigned server-side would make every tag look
 * deliberate when none of them are.
 */
export const TagColor = Schema.String.check(Schema.isPattern(/^#[0-9a-f]{6}$/));
export type TagColor = typeof TagColor.Type;

/**
 * What a tag may be attached to.
 *
 * A literal union, unlike the vocabulary itself, because the code genuinely
 * branches on it: each value names a different table to reconcile against when
 * its owner disappears, and `local_command` is the only one whose id needs a
 * scope to be unique.
 *
 * `local_command` entries live inside `projection_projects.local_actions_json`
 * rather than in a table of their own. That is normally disqualifying for
 * anything tagged — a projection is rebuilt from the event log — but a
 * `LocalActionId` is chosen by its author, travels in the event, and is already
 * the key of the desktop's on-disk acceptance store. It survives a rebuild.
 */
export const TaggableType = Schema.Literals(["agent", "memory", "project", "local_command"]);
export type TaggableType = typeof TaggableType.Type;

/**
 * The thing a tag is attached to.
 *
 * `scopeId` is what makes a `local_command` addressable: its id is unique only
 * within its project, so the project is part of the key. Everything else has a
 * globally unique id and carries `''`, which is a real value rather than a null
 * for the same reason `namespace` is — it sits in a primary key, and a primary
 * key column cannot be null.
 */
export const TagTarget = Schema.Struct({
  type: TaggableType,
  /** Owning project for `local_command`; `''` for every other type. */
  scopeId: Schema.String.check(Schema.isMaxLength(128)),
  id: TrimmedNonEmptyString.check(Schema.isMaxLength(128)),
});
export type TagTarget = typeof TagTarget.Type;

export const Tag = Schema.Struct({
  id: TagId,
  namespace: TagNamespace,
  slug: TagSlug,
  label: TrimmedNonEmptyString.check(Schema.isMaxLength(TAG_LABEL_MAX_LENGTH)),
  description: Schema.NullOr(
    TrimmedNonEmptyString.check(Schema.isMaxLength(TAG_DESCRIPTION_MAX_LENGTH)),
  ),
  color: Schema.NullOr(TagColor),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type Tag = typeof Tag.Type;

/**
 * A tag as the matching rule needs it: the id it is keyed by and the namespace
 * that decides which condition it answers.
 *
 * Deliberately not the whole `Tag`. The rule runs on every session start and in
 * the web's previews, and the label, colour and timestamps have nothing to do
 * with it — passing them would invite a caller to branch on a slug, which is
 * precisely what this design forbids.
 */
export const TagMatchRef = Schema.Struct({
  tagId: TagId,
  namespace: TagNamespace,
});
export type TagMatchRef = typeof TagMatchRef.Type;

// ── Admin wire shapes ────────────────────────────────────────────────

export const AdminTagCreateRequest = Schema.Struct({
  namespace: TagNamespace,
  slug: TagSlug,
  label: TrimmedNonEmptyString.check(Schema.isMaxLength(TAG_LABEL_MAX_LENGTH)),
  description: Schema.optional(
    Schema.NullOr(TrimmedNonEmptyString.check(Schema.isMaxLength(TAG_DESCRIPTION_MAX_LENGTH))),
  ),
  color: Schema.optional(Schema.NullOr(TagColor)),
});
export type AdminTagCreateRequest = typeof AdminTagCreateRequest.Type;

/**
 * `namespace` and `slug` are absent on purpose, as `scope` and `slug` are in
 * `AdminMemoryUpdateRequest`: together they are the tag's identity, and a
 * rename that silently re-pointed every assignment would be a different
 * operation wearing the same button.
 */
export const AdminTagUpdateRequest = Schema.Struct({
  label: Schema.optional(TrimmedNonEmptyString.check(Schema.isMaxLength(TAG_LABEL_MAX_LENGTH))),
  description: Schema.optional(
    Schema.NullOr(TrimmedNonEmptyString.check(Schema.isMaxLength(TAG_DESCRIPTION_MAX_LENGTH))),
  ),
  color: Schema.optional(Schema.NullOr(TagColor)),
});
export type AdminTagUpdateRequest = typeof AdminTagUpdateRequest.Type;

/**
 * Setting a target's tags is a replace, not an add.
 *
 * The editor that calls it shows every tag the thing has and lets a person
 * check and uncheck freely, so the payload is already the complete intended
 * set. An add/remove pair would make the screen responsible for computing a
 * diff it has no reason to know about, and would leave "remove the last tag"
 * expressible only as an empty add.
 */
export const AdminTagAssignmentRequest = Schema.Struct({
  target: TagTarget,
  tagIds: Schema.Array(TagId).check(Schema.isMaxLength(TAG_MAX_PER_TARGET)),
});
export type AdminTagAssignmentRequest = typeof AdminTagAssignmentRequest.Type;

/**
 * A listed tag carries whether it is retired, because the screen shows both and
 * a retired one is offered a "Reactivar" button instead of an "Editar" one. It
 * is a separate wrapper rather than a field on `Tag` so that nothing which only
 * ever handles live tags — every picker, every match — has to know the state
 * exists.
 */
export const AdminTagListEntry = Schema.Struct({
  tag: Tag,
  archivedAt: Schema.NullOr(IsoDateTime),
});
export type AdminTagListEntry = typeof AdminTagListEntry.Type;

export const AdminTagsResponse = Schema.Struct({ tags: Schema.Array(AdminTagListEntry) });
export type AdminTagsResponse = typeof AdminTagsResponse.Type;

export const AdminTagResponse = Schema.Struct({ tag: Tag });
export type AdminTagResponse = typeof AdminTagResponse.Type;

/**
 * What a screen needs to render tags for many things at once without a request
 * per thing: the vocabulary, plus who carries what.
 */
export const AdminTagAssignmentsResponse = Schema.Struct({
  assignments: Schema.Array(Schema.Struct({ target: TagTarget, tagIds: Schema.Array(TagId) })),
});
export type AdminTagAssignmentsResponse = typeof AdminTagAssignmentsResponse.Type;

/** Convenience for callers that address a project's local command. */
export const localCommandTagTarget = (input: {
  readonly projectId: ProjectId;
  readonly actionId: string;
}): TagTarget => ({ type: "local_command", scopeId: input.projectId, id: input.actionId });

/** The other three types share a shape: no scope, globally unique id. */
export const globalTagTarget = (
  type: Exclude<TaggableType, "local_command">,
  id: string,
): TagTarget => ({ type, scopeId: "", id });

/** `namespace:slug`, or just `slug` when unqualified. The display handle. */
export const formatTagRef = (tag: { readonly namespace: string; readonly slug: string }): string =>
  tag.namespace === "" ? tag.slug : `${tag.namespace}:${tag.slug}`;
